<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  getOfficeHours,
  saveOfficeHours,
} from 'dashboard/api/relayVoiceInbound';
import {
  DAYS,
  MAX_PERIODS,
  MAX_QUESTIONS,
  WEEKDAYS,
  fillReopen,
  formatOpening,
  scheduleProblems,
} from './officeHours';

// Office hours for chat and phone, and Mira's after-hours chat intake. Saved
// in Relay; Relay sends the same hours to the phone system for calls.
const { t } = useI18n();
const { accountId } = useAccount();

const state = ref('loading');
const saving = ref(false);
const hours = ref(null);
const timezones = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['Africa/Lagos'];
  }
})();

const problems = computed(() =>
  hours.value ? scheduleProblems(hours.value) : []
);
const preview = computed(() => {
  if (!hours.value) return [];
  const h = hours.value;
  const reopen = t('TEAM_LEAD.HOURS.CHAT.PREVIEW_REOPEN');
  const questions = h.intake_questions.filter(q => q.trim()).length
    ? h.intake_questions.filter(q => q.trim())
    : h.defaults.intake_questions;
  return [
    { from: 'customer', text: t('TEAM_LEAD.HOURS.CHAT.PREVIEW_CUSTOMER') },
    {
      from: 'assistant',
      text: fillReopen(h.closed_message || h.defaults.closed_message, reopen),
    },
    { from: 'assistant', text: questions[0] },
    { from: 'customer', text: t('TEAM_LEAD.HOURS.CHAT.PREVIEW_ACCOUNT') },
    {
      from: 'assistant',
      text: h.refusal_message || h.defaults.refusal_message,
    },
    ...(questions[1] ? [{ from: 'assistant', text: questions[1] }] : []),
    { from: 'more', text: t('TEAM_LEAD.HOURS.CHAT.PREVIEW_MORE') },
    {
      from: 'assistant',
      text: fillReopen(h.closing_message || h.defaults.closing_message, reopen),
    },
  ];
});
const statusLine = computed(() => {
  const now = hours.value?.now;
  if (!now) return '';
  if (now.reason === 'always_open') return t('TEAM_LEAD.HOURS.STATUS.ALWAYS');
  if (now.open) return t('TEAM_LEAD.HOURS.STATUS.OPEN');
  const key = now.reason === 'holiday' ? 'HOLIDAY' : 'CLOSED';
  return t(`TEAM_LEAD.HOURS.STATUS.${key}`, {
    when: now.next_open
      ? formatOpening(now.next_open, hours.value.timezone)
      : t('TEAM_LEAD.HOURS.STATUS.UNKNOWN'),
  });
});

const load = async () => {
  try {
    const result = await getOfficeHours(accountId.value);
    hours.value = {
      ...result,
      weekly: Object.fromEntries(
        DAYS.map(day => [
          day,
          (result.weekly?.[day] || []).map(p => ({ ...p })),
        ])
      ),
      holidays: (result.holidays || []).map(h => ({ ...h })),
      intake_questions: [...(result.intake_questions || [])],
      closed_message: result.closed_message || '',
      refusal_message: result.refusal_message || '',
      closing_message: result.closing_message || '',
    };
    state.value = 'ok';
  } catch {
    state.value = 'error';
  }
};

const addPeriod = day => {
  const list = hours.value.weekly[day];
  if (list.length >= MAX_PERIODS) return;
  const last = list[list.length - 1];
  list.push(
    last
      ? { open: last.close, close: '17:00' }
      : { open: '08:00', close: '17:00' }
  );
};
const removePeriod = (day, index) => hours.value.weekly[day].splice(index, 1);
const copyMonday = () => {
  WEEKDAYS.slice(1).forEach(day => {
    hours.value.weekly[day] = hours.value.weekly.mon.map(p => ({ ...p }));
  });
};
const addHoliday = () => hours.value.holidays.push({ date: '', name: '' });
const removeHoliday = index => hours.value.holidays.splice(index, 1);
const addQuestion = () => {
  if (!hours.value.intake_questions.length)
    hours.value.intake_questions = [...hours.value.defaults.intake_questions];
  else if (hours.value.intake_questions.length < MAX_QUESTIONS)
    hours.value.intake_questions.push('');
};
const removeQuestion = index => hours.value.intake_questions.splice(index, 1);

const save = async () => {
  if (problems.value.length) return;
  saving.value = true;
  const h = hours.value;
  try {
    const result = await saveOfficeHours(accountId.value, {
      enabled: h.enabled,
      timezone: h.timezone,
      weekly: h.weekly,
      holidays: h.holidays.filter(day => day.date),
      after_hours_chat_enabled: h.after_hours_chat_enabled,
      closed_message: h.closed_message,
      intake_questions: h.intake_questions.filter(q => q.trim()),
      refusal_message: h.refusal_message,
      closing_message: h.closing_message,
    });
    const voice = result.voice?.status;
    useAlert(
      voice === 'synced' || voice === 'not_configured'
        ? t('TEAM_LEAD.HOURS.SAVED')
        : t('TEAM_LEAD.HOURS.SAVED_PHONE_PENDING')
    );
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || t('TEAM_LEAD.GENERIC_ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
defineExpose({ load });
</script>

<template>
  <woot-loading-state
    v-if="state === 'loading'"
    :message="t('TEAM_LEAD.LOADING')"
  />
  <p
    v-else-if="state === 'error'"
    class="rounded-xl bg-n-alpha-1 px-5 py-4 text-sm text-n-slate-11"
  >
    {{ t('TEAM_LEAD.HOURS.UNAVAILABLE') }}
  </p>
  <div v-else class="flex flex-col gap-5">
    <section
      data-tour="office-hours"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-5"
    >
      <header class="flex items-start justify-between gap-4 flex-wrap">
        <div class="flex flex-col gap-1 max-w-xl">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('TEAM_LEAD.HOURS.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('TEAM_LEAD.HOURS.DESCRIPTION') }}
          </p>
        </div>
        <span
          class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
          :class="
            hours.now?.open
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-iris-3 text-n-iris-11'
          "
          role="status"
          data-test-id="hours-status"
        >
          <span
            :class="hours.now?.open ? 'i-ph-sun-bold' : 'i-ph-moon-bold'"
            class="size-3.5"
            aria-hidden="true"
          />
          {{ statusLine }}
        </span>
      </header>

      <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
        <label class="flex items-center gap-3 text-sm text-n-slate-12">
          <Switch v-model="hours.enabled" data-test-id="hours-enabled" />
          {{ t('TEAM_LEAD.HOURS.ENABLED') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          {{ t('TEAM_LEAD.HOURS.TIMEZONE') }}
          <select
            v-model="hours.timezone"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          >
            <option v-for="zone in timezones" :key="zone" :value="zone">
              {{ zone }}
            </option>
          </select>
        </label>
      </div>
      <p v-if="!hours.enabled" class="text-xs text-n-slate-10 -mt-2">
        {{ t('TEAM_LEAD.HOURS.OFF_HINT') }}
      </p>

      <div class="flex flex-col divide-y divide-n-weak" data-tour="hours-week">
        <div
          v-for="day in DAYS"
          :key="day"
          class="flex flex-wrap items-center gap-3 py-2.5"
        >
          <span class="w-28 text-sm font-medium text-n-slate-12">
            {{ t(`TEAM_LEAD.HOURS.DAYS.${day.toUpperCase()}`) }}
          </span>
          <span
            v-if="!hours.weekly[day].length"
            class="text-sm text-n-slate-10"
          >
            {{ t('TEAM_LEAD.HOURS.CLOSED_ALL_DAY') }}
          </span>
          <div
            v-for="(period, index) in hours.weekly[day]"
            :key="index"
            class="flex items-center gap-1.5"
          >
            <input
              v-model="period.open"
              type="time"
              :aria-label="t('TEAM_LEAD.HOURS.OPENS', { day })"
              class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 tabular-nums"
            />
            <span class="text-n-slate-10" aria-hidden="true">–</span>
            <input
              v-model="period.close"
              type="time"
              :aria-label="t('TEAM_LEAD.HOURS.CLOSES', { day })"
              class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 tabular-nums"
            />
            <Button
              icon="i-ph-x-bold"
              variant="ghost"
              color="slate"
              size="xs"
              :aria-label="t('TEAM_LEAD.HOURS.REMOVE_PERIOD')"
              @click="removePeriod(day, index)"
            />
          </div>
          <Button
            v-if="hours.weekly[day].length < MAX_PERIODS"
            :label="t('TEAM_LEAD.HOURS.ADD_PERIOD')"
            icon="i-ph-plus-bold"
            variant="link"
            size="xs"
            @click="addPeriod(day)"
          />
        </div>
      </div>
      <Button
        :label="t('TEAM_LEAD.HOURS.COPY_MONDAY')"
        variant="faded"
        color="slate"
        size="sm"
        class="self-start"
        @click="copyMonday"
      />

      <div class="flex flex-col gap-2" data-tour="hours-holidays">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('TEAM_LEAD.HOURS.HOLIDAYS') }}
        </h3>
        <p class="text-xs text-n-slate-10">
          {{ t('TEAM_LEAD.HOURS.HOLIDAYS_HINT') }}
        </p>
        <div
          v-for="(holiday, index) in hours.holidays"
          :key="index"
          class="flex flex-wrap items-center gap-2"
        >
          <input
            v-model="holiday.date"
            type="date"
            :aria-label="t('TEAM_LEAD.HOURS.HOLIDAY_DATE')"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12"
          />
          <input
            v-model="holiday.name"
            maxlength="80"
            :placeholder="t('TEAM_LEAD.HOURS.HOLIDAY_NAME')"
            :aria-label="t('TEAM_LEAD.HOURS.HOLIDAY_NAME')"
            class="!mb-0 flex-1 min-w-40 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12"
          />
          <Button
            icon="i-ph-trash-bold"
            variant="ghost"
            color="slate"
            size="xs"
            :aria-label="t('TEAM_LEAD.HOURS.REMOVE_HOLIDAY')"
            @click="removeHoliday(index)"
          />
        </div>
        <Button
          :label="t('TEAM_LEAD.HOURS.ADD_HOLIDAY')"
          icon="i-ph-plus-bold"
          variant="link"
          size="xs"
          class="self-start"
          @click="addHoliday"
        />
      </div>
    </section>

    <section
      data-tour="after-hours-chat"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-5"
    >
      <header class="flex items-start justify-between gap-4 flex-wrap">
        <div class="flex flex-col gap-1 max-w-xl">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('TEAM_LEAD.HOURS.CHAT.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('TEAM_LEAD.HOURS.CHAT.DESCRIPTION') }}
          </p>
        </div>
        <label class="flex items-center gap-3 text-sm text-n-slate-12">
          <Switch
            v-model="hours.after_hours_chat_enabled"
            data-test-id="after-hours-chat-enabled"
          />
          {{ t('TEAM_LEAD.HOURS.CHAT.ENABLED') }}
        </label>
      </header>
      <p
        class="flex items-start gap-2 rounded-lg bg-n-alpha-1 px-4 py-3 text-xs text-n-slate-11"
      >
        <span class="i-ph-shield-check-bold size-4 shrink-0 text-n-teal-10" />
        {{ t('TEAM_LEAD.HOURS.CHAT.SAFETY') }}
      </p>

      <div class="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <div class="flex flex-col gap-4">
          <TextArea
            v-model="hours.closed_message"
            :label="t('TEAM_LEAD.HOURS.CHAT.CLOSED_MESSAGE')"
            :placeholder="hours.defaults.closed_message"
            :max-length="1000"
            :message="
              t('TEAM_LEAD.HOURS.CHAT.REOPEN_HINT', {
                placeholder: hours.reopen_placeholder,
              })
            "
            auto-height
          />
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('TEAM_LEAD.HOURS.CHAT.QUESTIONS') }}
            </span>
            <p class="text-xs text-n-slate-10">
              {{ t('TEAM_LEAD.HOURS.CHAT.QUESTIONS_HINT') }}
            </p>
            <ol
              v-if="!hours.intake_questions.length"
              class="flex flex-col gap-1 list-decimal ps-5 text-sm text-n-slate-11"
            >
              <li
                v-for="question in hours.defaults.intake_questions"
                :key="question"
              >
                {{ question }}
              </li>
            </ol>
            <div
              v-for="(question, index) in hours.intake_questions"
              :key="index"
              class="flex items-start gap-2"
            >
              <span class="pt-2 text-xs text-n-slate-10 tabular-nums">
                {{ index + 1 }}.
              </span>
              <textarea
                v-model="hours.intake_questions[index]"
                rows="2"
                maxlength="300"
                :aria-label="
                  t('TEAM_LEAD.HOURS.CHAT.QUESTION', { n: index + 1 })
                "
                class="!mb-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
              />
              <Button
                icon="i-ph-trash-bold"
                variant="ghost"
                color="slate"
                size="xs"
                :aria-label="t('TEAM_LEAD.HOURS.CHAT.REMOVE_QUESTION')"
                @click="removeQuestion(index)"
              />
            </div>
            <Button
              v-if="hours.intake_questions.length < MAX_QUESTIONS"
              :label="
                hours.intake_questions.length
                  ? t('TEAM_LEAD.HOURS.CHAT.ADD_QUESTION')
                  : t('TEAM_LEAD.HOURS.CHAT.EDIT_QUESTIONS')
              "
              icon="i-ph-pencil-simple-bold"
              variant="link"
              size="xs"
              class="self-start"
              @click="addQuestion"
            />
          </div>
          <TextArea
            v-model="hours.refusal_message"
            :label="t('TEAM_LEAD.HOURS.CHAT.REFUSAL')"
            :placeholder="hours.defaults.refusal_message"
            :max-length="1000"
            :message="t('TEAM_LEAD.HOURS.CHAT.REFUSAL_HINT')"
            auto-height
          />
          <TextArea
            v-model="hours.closing_message"
            :label="t('TEAM_LEAD.HOURS.CHAT.CLOSING')"
            :placeholder="hours.defaults.closing_message"
            :max-length="1000"
            auto-height
          />
        </div>

        <aside
          class="flex flex-col gap-2 rounded-xl bg-n-alpha-1 p-4 self-start"
          :aria-label="t('TEAM_LEAD.HOURS.CHAT.PREVIEW')"
          data-test-id="chat-preview"
        >
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('TEAM_LEAD.HOURS.CHAT.PREVIEW') }}
          </span>
          <template v-for="(line, index) in preview" :key="index">
            <p
              v-if="line.from === 'more'"
              class="text-center text-[0.6875rem] text-n-slate-10"
            >
              {{ line.text }}
            </p>
            <p
              v-else
              class="max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed"
              :class="
                line.from === 'customer'
                  ? 'self-start bg-n-slate-4 text-n-slate-12'
                  : 'self-end bg-n-solid-iris text-n-slate-12'
              "
            >
              {{ line.text }}
            </p>
          </template>
        </aside>
      </div>
    </section>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        :label="t('TEAM_LEAD.HOURS.SAVE')"
        :is-loading="saving"
        :disabled="problems.length > 0"
        data-test-id="hours-save"
        @click="save"
      />
      <ul v-if="problems.length" role="alert" class="text-xs text-n-ruby-11">
        <li v-for="problem in problems" :key="problem.key + problem.day">
          {{
            t(`TEAM_LEAD.HOURS.PROBLEMS.${problem.key}`, {
              day: problem.day
                ? t(`TEAM_LEAD.HOURS.DAYS.${problem.day.toUpperCase()}`)
                : '',
            })
          }}
        </li>
      </ul>
      <p
        v-else-if="hours.voice_sync_error"
        role="alert"
        class="text-xs text-n-amber-11"
      >
        {{ hours.voice_sync_error }}
      </p>
      <p v-else-if="hours.voice_synced_at" class="text-xs text-n-slate-10">
        {{ t('TEAM_LEAD.HOURS.PHONE_SYNCED') }}
      </p>
    </div>
  </div>
</template>
